import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useTheme, styled } from '@mui/material/styles';
import { Grid, Stack, Typography, Link, useMediaQuery, Box, Button, FormHelperText, TextField } from '@mui/material';

// project imports
import AuthCardWrapper from '../../components/Cards/AuthCardWrapper';
import logoImg from '../../assets/images/raptor_tech_logo.png';
import ApiHelper from 'utils/apiHelper';

const AuthWrapper = styled('div')(({ theme }) => ({
    background: `linear-gradient(white, ${theme.palette.primary.main})`
    // minHeight: '100vh'
}));

const ForgotPassword = () => {
    const { userInfo } = useSelector((state) => state.auth);
    const theme = useTheme();
    const matchDownSM = useMediaQuery(theme.breakpoints.down('md'));

    const navigate = useNavigate();

    useEffect(() => {
        if (userInfo) {
            navigate('/home');
        }
    }, [navigate, userInfo]);

    const formik = useFormik({
        initialValues: {
            username: '',
            email: '',
            submit: null
        },
        validationSchema: Yup.object({
            username: Yup.string().max(255).required('Username is required'),
            email: Yup.string().email('Must be a valid email').max(255).required('Email is required')
        }),
        onSubmit: (values, helpers) => {
            ApiHelper.post('/api/auth/forgot_password/', { email: values.email, username: values.username }, {}, {}, false)
                .then(() => {
                    toast.success('Successfully done, please check your email');
                    helpers.setSubmitting(false);
                })
                .catch(() => {
                    toast.error('Failed to send email!');
                    helpers.setSubmitting(false);
                });
        }
    });

    return (
        <AuthWrapper>
            <Grid container direction="column" justifyContent="flex-end" sx={{ minHeight: '100vh' }}>
                <Grid item xs={12}>
                    <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: 'calc(100vh - 68px)' }}>
                        <Grid item sx={{ m: { xs: 1, sm: 3 }, mb: 0 }}>
                            <AuthCardWrapper>
                                <Grid container spacing={2} alignItems="center" justifyContent="center">
                                    <Grid item sx={{ mb: 3 }}>
                                        <Link href="/">
                                            <img src={logoImg} width="150" alt="" />
                                        </Link>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Grid
                                            container
                                            direction={matchDownSM ? 'column-reverse' : 'row'}
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <Grid item>
                                                <Stack alignItems="center" justifyContent="center" spacing={1}>
                                                    <Typography
                                                        color={theme.palette.primary.main}
                                                        gutterBottom
                                                        variant={matchDownSM ? 'h3' : 'h2'}
                                                    >
                                                        WakeSMART Administration Login
                                                    </Typography>
                                                    {/* <Box sx={{ mb: 2 }}> */}
                                                    <Typography variant="subtitle1">Please provide your email address</Typography>
                                                    {/* </Box> */}
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <form noValidate onSubmit={formik.handleSubmit}>
                                            <TextField
                                                error={Boolean(formik.touched.username && formik.errors.username)}
                                                fullWidth
                                                helperText={formik.touched.username && formik.errors.username}
                                                label="Username"
                                                margin="normal"
                                                name="username"
                                                onBlur={formik.handleBlur}
                                                onChange={formik.handleChange}
                                                type="username"
                                                value={formik.values.username}
                                            />
                                            <TextField
                                                error={Boolean(formik.touched.email && formik.errors.email)}
                                                fullWidth
                                                helperText={formik.touched.email && formik.errors.email}
                                                label="Email"
                                                margin="normal"
                                                name="email"
                                                onBlur={formik.handleBlur}
                                                onChange={formik.handleChange}
                                                type="email"
                                                value={formik.values.email}
                                            />
                                            {formik.errors.submit && (
                                                <Box sx={{ mt: 3 }}>
                                                    <FormHelperText error>{formik.errors.submit}</FormHelperText>
                                                </Box>
                                            )}
                                            <Box sx={{ mt: 2 }}>
                                                <Button
                                                    disabled={formik.isSubmitting}
                                                    fullWidth
                                                    size="large"
                                                    type="submit"
                                                    variant="contained"
                                                >
                                                    Submit
                                                </Button>
                                            </Box>
                                        </form>
                                    </Grid>
                                </Grid>
                            </AuthCardWrapper>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </AuthWrapper>
    );
};

export default ForgotPassword;
